import { Injectable, inject } from "@angular/core";
import {
    LogexPivotService,
    ILogexPivotDefinition,
    PivotProviderBase
} from "@logex/framework/lg-pivot";
import { AssignApplicationRolesComponent } from "../assign-application-roles.component";
import {
    AssignRolesPivotInstance,
    AssignRolesPivotProduct,
    AssignRolesPivotRole
} from "./assign-application-roles-pivot.types";
import { SharedDefinitions } from "@mrdm-self-service-frontend/shared/data-access-definitions";
import { ApplicationRole, AvailableApplicationRole } from "../../../types";
import { getAppRoleKey } from "../../../helpers/application-role-key";
import { getRoleLevel, getSortName } from "../../../helpers/application-role-hierarchy";

// ----------------------------------------------------------------------------------
//
@Injectable()
export class AssignApplicationRolesPivot extends PivotProviderBase {
    private _definitions = inject(SharedDefinitions);


    getPivotDefinition(): ILogexPivotDefinition {
        return <ILogexPivotDefinition>{
            column: "productCode",
            levelId: "products",
            defaultOrderBy: "name",
            store: "products",
            attachedColumns: [
                (node: AssignRolesPivotProduct, row: AvailableApplicationRole) => {
                    node.name = this._definitions.product[row.productCode].name;
                    node.$expanded = false;
                }
            ],
            children: {
                column: "applicationInstanceCode",
                levelId: "applicationInstances",
                defaultOrderBy: "name",
                store: "applicationInstances",
                attachedColumns: [
                    (node: AssignRolesPivotInstance, row: AvailableApplicationRole) => {
                        node.name =
                            this._definitions.applicationInstance[row.applicationInstanceCode].name;
                        node.$expanded = false;
                    }
                ],
                children: {
                    column: "applicationRoleCode",
                    levelId: "applicationRoles",
                    defaultOrderBy: "sortName",
                    store: "applicationRoles",
                    attachedColumns: [
                        (
                            node: AssignRolesPivotRole,
                            row: AvailableApplicationRole,
                            _parent,
                            ctx: AssignApplicationRolesComponent
                        ) => {
                            const appRole =
                                ctx._definitions.applicationRole[row.applicationRoleCode];
                            node.name = appRole?.name;
                            node.description = appRole?.description || "";

                            node.selected = this._isRoleSelected(
                                row,
                                ctx._initiallySelectedDictionary
                            );
                            node.assignedViaBr = ctx._assignedByBusinessRole[getAppRoleKey(row)];
                            node.notEditableInSSP =
                                !this._definitions.applicationRole[node.applicationRoleCode]
                                    ?.isSspEditable;
                            node.disabled = node.assignedViaBr || node.notEditableInSSP;
                            node.level = getRoleLevel(row, ctx._availableRolesDictionary);
                            node.sortName = getSortName(
                                node,
                                ctx._availableRolesDictionary,
                                this._definitions
                            );
                        }
                    ],
                    filters: (ctx: AssignApplicationRolesComponent) => {
                        return {
                            applicationInstanceCode: (
                                _applicationInstanceCode,
                                role: AssignRolesPivotRole
                            ) => {
                                return (
                                    !ctx._filteredApplicationInstances ||
                                    ctx._filteredApplicationInstances.$empty ||
                                    role.applicationInstanceCode in
                                        ctx._filteredApplicationInstances
                                );
                            },
                            applicationRoleCode: (
                                _applicationRoleCode,
                                role: AssignRolesPivotRole
                            ) => {
                                return (
                                    !ctx._filteredApplicationRoles ||
                                    ctx._filteredApplicationRoles.$empty ||
                                    role.applicationRoleCode in ctx._filteredApplicationRoles
                                );
                            },
                            productCode: (_productCode, role: AssignRolesPivotRole) => {
                                return (
                                    !ctx._filteredProducts ||
                                    ctx._filteredProducts.$empty ||
                                    role.productCode in ctx._filteredProducts
                                );
                            },
                            selected: (_selected, role: AssignRolesPivotRole) => {
                                return !ctx._showOnlySelected || role.selected;
                            }
                        };
                    }
                }
            }
        };
    }

    private _isRoleSelected(
        role: AvailableApplicationRole,
        selectedRoles: Record<string, ApplicationRole>
    ): boolean {
        return !!selectedRoles[getAppRoleKey(role)];
    }
}
